import type { IAccessTokenProvider } from "../../application/interfaces/IAccessTokenProvider";
import type { IAppConfigProvider } from "../../application/interfaces/IAppConfigProvider";
import type { ICartRepository } from "../../application/interfaces/ICartRepository";
import type { CartCreateDto } from "../../shared/CartCreateDto";
import type { CartDto } from "../../shared/CartDto";
import type { CartPartnerDto } from "../../shared/CartPartnerDto";
import type { CartPatchDto } from "../../shared/CartPatchDto";
import type { CartSpecialPriceDto } from "../../shared/CartSpecialPriceDto";
import type { PartyAddressDto } from "../../shared/PartyAddressDto";

export class CartRepository implements ICartRepository {
  readonly configProvider: IAppConfigProvider;
  readonly tokenProvider: IAccessTokenProvider;

  constructor(
    configProvider: IAppConfigProvider,
    tokenProvider: IAccessTokenProvider,
  ) {
    this.configProvider = configProvider;
    this.tokenProvider = tokenProvider;
  }

  async create(createDto: CartCreateDto): Promise<CartDto | null> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart`;
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(createDto),
    });
    if (response.ok) {
      return (await response.json()) as CartDto;
    } else {
      const content = await response.json();
      if ("message" in content){
        throw new Error(content.message);
      } else {
        const msg = await response.text();
        throw new Error(msg);
      }
    }
  }
  async update(cartReference: string, patchDto: CartPatchDto): Promise<CartDto | null> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
      "Content-Type": "application/json",
      "Accept": "application/json"
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}`;
    const response = await fetch(url, {
      method: "PATCH",
      headers,
      body: JSON.stringify(patchDto),
    });
    if (response.ok) {
      return (await response.json()) as CartDto;
    } else {
      const content = await response.json();
      if ("message" in content){
        throw new Error(content.message);
      } else {
        const msg = await response.text();
        throw new Error(msg);
      }
    }
  }

  async getByReference(cartReference: string): Promise<CartDto | null> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}`;
    const response = await fetch(url, {
      headers,
    });
    if (response.ok) {
      return (await response.json()) as CartDto;
    } else {
      throw new Error(
        `Failed to fetch sender: HTTP ${response.status} ${response.statusText}`,
      );
    }
  }

  async getPartners(cartReference: string): Promise<CartPartnerDto[]> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}/Partner`;
    const response = await fetch(url, {
      headers,
    });
    if (response.ok) {
      return (await response.json()) as CartPartnerDto[];
    } else {
      throw new Error(
        `Failed to fetch sender: HTTP ${response.status} ${response.statusText}`,
      );
    }
  }

  async getCustomerSpecialPrices(
    cartReference: string,
  ): Promise<CartSpecialPriceDto[]> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}/CustomerSpecialPrice`;
    const response = await fetch(url, {
      headers,
    });
    if (response.ok) {
      return (await response.json()) as CartSpecialPriceDto[];
    } else {
      throw new Error(
        `Failed to fetch sender: HTTP ${response.status} ${response.statusText}`,
      );
    }
  }

  async getPartnerSpecialPrices(
    cartReference: string,
  ): Promise<CartSpecialPriceDto[]> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}/PartnerSpecialPrice`;
    const response = await fetch(url, {
      headers,
    });
    if (response.ok) {
      return (await response.json()) as CartSpecialPriceDto[];
    } else {
      throw new Error(
        `Failed to fetch sender: HTTP ${response.status} ${response.statusText}`,
      );
    }
  }

  async setCustomerAddress(
    cartReference: string,
    address: PartyAddressDto | null,
  ): Promise<CartDto | null> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}/CustomerAddress`;
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(address),
    });
    if (response.ok) {
      return (await response.json()) as CartDto;
    } else {
        const msg = await response.text();
        throw new Error(msg);
    }
  }

  async setPartnerAddress(
    cartReference: string,
    address: PartyAddressDto | null,
  ): Promise<CartDto | null> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}/PartnerAddress`;
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(address),
    });
    if (response.ok) {
      return (await response.json()) as CartDto;
    } else {
        const msg = await response.text();
        throw new Error(msg);
    }
  }


  async submitOrder(
    cartReference: string,
    agbAccepted: boolean,
  ): Promise<CartDto | null> {
    const token = await this.tokenProvider.getAccessToken();
    const config = await this.configProvider.getAppConfig();
    const headers: Record<string, string> = {
      "X-Api-Key": config.apiKey,
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.apiUrl}/Cart/${cartReference}/Submit`;
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({agbAccepted: agbAccepted}),
    });
    if (response.ok) {
      return (await response.json()) as CartDto;
    } else {
      throw new Error(
        `Failed create Cart: HTTP ${response.status} ${response.statusText}`,
      );
    }
  }
}
